import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { toast } from "../components/Toast";
import { ENDPOINTS } from "../config";

const STORAGE_KEY = "fonexpress_compare";
const CompareContext = createContext(null);

function loadItems() {
    try {
        const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        return Array.isArray(raw) ? raw.slice(0, 2) : [];
    } catch {
        return [];
    }
}

function saveItems(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, 2)));
}

function sharesFamily(a, b) {
    const ids = new Set((a?.family || []).map((f) => f.id));
    return (b?.family || []).some((f) => ids.has(f.id));
}

export function CompareProvider({ children }) {
    const [items, setItems] = useState(loadItems);
    const [pendingReplace, setPendingReplace] = useState(null);

    const persist = useCallback((next) => {
        saveItems(next);
        setItems(next);
    }, []);

    const remove = useCallback((slug) => {
        persist(loadItems().filter((item) => item.slug !== slug));
    }, [persist]);

    const clear = useCallback(() => {
        persist([]);
    }, [persist]);

    const add = useCallback(async (slug) => {
        const clean = String(slug || "").trim().toLowerCase();
        if (!clean) {
            toast.error("This product cannot be compared yet.");
            return false;
        }
        const current = loadItems();
        if (current.some((item) => item.slug === clean)) {
            toast.info("Already in compare.");
            return false;
        }

        const res = await fetch(`${ENDPOINTS.COMPARE.ITEM}?slug=${encodeURIComponent(clean)}`);
        const body = await res.json().catch(() => ({}));
        if (!res.ok || !body.isOk) {
            toast.error(body.message || "Could not add this device to compare.");
            return false;
        }
        const incoming = body.data;

        if (current.length >= 1) {
            const firstRes = await fetch(
                `${ENDPOINTS.COMPARE.ITEM}?slug=${encodeURIComponent(current[0].slug)}`,
            );
            const firstBody = await firstRes.json().catch(() => ({}));
            if (firstRes.ok && firstBody?.data?.family) {
                current[0] = { ...current[0], family: firstBody.data.family };
            }
        }

        const mismatch = "You can only compare devices from the same category (for example two phones, not a phone and a TV).";
        if (current.length >= 1 && !sharesFamily(current[0], incoming)) {
            toast.error(mismatch);
            return false;
        }

        let next;
        if (current.length >= 2) {
            const ok = await new Promise((resolve) => {
                setPendingReplace({ name: incoming.name, resolve });
            });
            if (!ok) return false;
            next = [current[0], incoming];
        } else {
            next = [...current, incoming];
        }

        persist(next.slice(0, 2));
        toast.success(`${incoming.name} added to compare.`);
        return true;
    }, [persist]);

    const syncFamilies = useCallback((serverItems) => {
        const current = loadItems();
        let changed = false;
        const next = current.map((item) => {
            const fresh = (serverItems || []).find((row) => row.slug === item.slug);
            if (!fresh?.family) return item;
            const oldIds = (item.family || []).map((entry) => entry.id).join(",");
            const newIds = fresh.family.map((entry) => entry.id).join(",");
            if (oldIds === newIds) return item;
            changed = true;
            return { ...item, family: fresh.family };
        });
        if (changed) persist(next);
    }, [persist]);

    const value = useMemo(
        () => ({ items, count: items.length, add, remove, clear, syncFamilies }),
        [items, add, remove, clear, syncFamilies],
    );

    const closeReplace = (ok) => {
        pendingReplace?.resolve(ok);
        setPendingReplace(null);
    };

    return (
        <CompareContext.Provider value={value}>
            {children}
            {pendingReplace && (
                <div
                    className="fixed inset-0 z-[80] flex items-center justify-center p-4"
                    style={{ background: "rgba(0,0,0,0.45)" }}
                    onClick={(event) => {
                        if (event.target === event.currentTarget) closeReplace(false);
                    }}
                >
                    <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md p-6">
                        <h2 className="text-base font-bold text-foreground">Replace the second device?</h2>
                        <p className="text-sm text-muted-foreground mt-2">
                            You can only compare two at a time. {pendingReplace.name} will replace the second device.
                        </p>
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => closeReplace(false)}
                                className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => closeReplace(true)}
                                className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:brightness-110"
                            >
                                Replace
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </CompareContext.Provider>
    );
}

export function useCompare() {
    const ctx = useContext(CompareContext);
    if (!ctx) throw new Error("useCompare must be used inside CompareProvider");
    return ctx;
}
